export type WindQuality =
  | "offshore"
  | "cross-offshore"
  | "cross-shore"
  | "cross-onshore"
  | "onshore";

export function waveHeightDescriptor(heightM: number): string {
  if (heightM < 0.3) return "Flat";
  if (heightM < 0.6) return "Ankle to knee high";
  if (heightM < 1.0) return "Waist high";
  if (heightM < 1.5) return "Chest to head high";
  if (heightM < 2.0) return "Head high+";
  if (heightM < 2.5) return "Overhead";
  if (heightM < 3.5) return "Double overhead";
  return "Triple overhead+";
}

export function waveHeightFt(heightM: number): string {
  const minFt = Math.round(heightM * 3.28084 * 2) / 2;
  const maxFt = Math.round(heightM * 1.15 * 3.28084 * 2) / 2;
  if (minFt === maxFt) return `${minFt} ft`;
  return `${minFt}-${maxFt} ft`;
}

function angleDiff(a: number, b: number): number {
  const d = Math.abs(((((a - b) % 360) + 360) % 360));
  return d > 180 ? 360 - d : d;
}

export function windQualityLabel(
  windDirectionDeg: number,
  beachFacingDeg: number | null
): { label: string; quality: WindQuality; score: number } {
  if (beachFacingDeg === null) {
    return { label: "Variable", quality: "cross-shore", score: 40 };
  }
  const offshoreIdeal = (beachFacingDeg + 180) % 360;
  const diff = angleDiff(windDirectionDeg, offshoreIdeal);

  if (diff <= 45) return { label: "Offshore", quality: "offshore", score: 100 };
  if (diff <= 90)
    return { label: "Cross-offshore", quality: "cross-offshore", score: 75 };
  if (diff <= 120)
    return { label: "Cross-shore", quality: "cross-shore", score: 40 };
  if (diff <= 150)
    return { label: "Cross-onshore", quality: "cross-onshore", score: 20 };
  return { label: "Onshore", quality: "onshore", score: 0 };
}

export function swellTypeLabel(periodS: number): string {
  if (periodS < 8) return "Wind swell";
  if (periodS < 12) return "Mid-period";
  return "Ground swell";
}

export function wetsuitRecommendation(waterTempC: number | null): string {
  if (waterTempC === null) return "Check locally";
  if (waterTempC > 22) return "Boardshorts";
  if (waterTempC >= 19) return "Shorty / spring suit";
  if (waterTempC >= 16) return "3/2mm full suit";
  if (waterTempC >= 12) return "4/3mm full suit";
  if (waterTempC >= 8) return "5/4mm + boots";
  return "6/5mm + boots, gloves, hood";
}

export type QualityTag = {
  text: string;
  variant: "positive" | "neutral" | "negative";
};

export function generateQualityTags(params: {
  windSpeedKmh: number;
  windQuality: WindQuality;
  swellPeriodS: number;
  tideState: string | null;
  tidePreference: string | null;
  waveHeightM: number;
  sweetSpotUpperM: number;
  sweetSpotMinM?: number;
}): QualityTag[] {
  const tags: QualityTag[] = [];
  const {
    windSpeedKmh,
    windQuality,
    swellPeriodS,
    tideState,
    tidePreference,
    waveHeightM,
    sweetSpotUpperM,
    sweetSpotMinM,
  } = params;

  const flatThreshold = Math.min(0.5, sweetSpotMinM ?? 0.5);
  const tooSmall = waveHeightM < flatThreshold;

  if (tooSmall) {
    tags.push({ text: "Too small", variant: "negative" });
    if (tideState) {
      const isBest =
        tidePreference &&
        tideState.toLowerCase().includes(tidePreference.split("-")[0]);
      tags.push({
        text: isBest ? `${tideState} (best)` : tideState,
        variant: isBest ? "positive" : "neutral",
      });
    }
    return tags.slice(0, 5);
  }

  if (windQuality === "offshore" && windSpeedKmh < 19) {
    tags.push({ text: "Clean face", variant: "positive" });
  }
  if (windSpeedKmh < 8) {
    tags.push({ text: "Glassy", variant: "positive" });
  }
  if (swellPeriodS >= 14) {
    tags.push({ text: "Long period", variant: "positive" });
  } else if (swellPeriodS >= 12) {
    tags.push({ text: "Ground swell", variant: "positive" });
  }
  if (windQuality === "onshore" && windSpeedKmh > 30) {
    tags.push({ text: "Blown out", variant: "negative" });
  } else if (windQuality === "onshore" && windSpeedKmh > 22) {
    tags.push({ text: "Choppy", variant: "negative" });
  }
  if (waveHeightM > sweetSpotUpperM) {
    tags.push({ text: "Big", variant: "neutral" });
  }
  if (tideState) {
    const isBest =
      tidePreference &&
      tideState.toLowerCase().includes(tidePreference.split("-")[0]);
    tags.push({
      text: isBest ? `${tideState} (best)` : tideState,
      variant: isBest ? "positive" : "neutral",
    });
  }

  return tags.slice(0, 5);
}

